import React, { useCallback, useState, useEffect } from 'react'
import { type NodeProps } from '@xyflow/react'
import type { StoryboardFlowNode } from '../../../types/mindmap-components'
import { useIdeaActions } from '../IdeaActions/useIdeaActions'
import { useStoryboardCanvasExport } from './hooks/useStoryboardCanvasExport'
import { useZoomState } from '../hooks/useZoomState'
import { useNodeActions } from '../../../context/nodeActions'
import { useReadOnlyContext } from '../../../context/readOnly'
import { useFeatureAccessContext } from '../../../context/featureAccess'
import { useNodeLabels } from './Base/hooks/useNodeLabels'
import { StoryboardCollapsedView } from './StoryboardNode/StoryboardCollapsedView'
import { StoryboardGeneratingView } from './StoryboardNode/StoryboardGeneratingView'
import { StoryboardFullView } from './StoryboardNode/StoryboardFullView'

/**
 * Storyboard node component - displays a 4-panel visual narrative
 *
 * Shows a 2x2 grid of storyboard images that were generated for an idea.
 * Clicking the node opens the full storyboard modal for viewing.
 * Storyboard nodes are leaf nodes (no children) and are always connected to their parent idea.
 */
export const StoryboardNode = React.memo((props: NodeProps<StoryboardFlowNode>) => {
  const { data: nodeData, id: nodeId } = props

  // Core actions and context
  const { viewStoryboardNode } = useIdeaActions()
  const { handleDeleteNode } = useNodeActions()
  const { isReadOnly } = useReadOnlyContext()
  const { isFeatureEnabled } = useFeatureAccessContext()
  const labelsEnabled = isFeatureEnabled('labels')

  // Label management (reuse shared hook from Base)
  const { mindmapId, labels } = nodeData
  const {
    labelContext,
    handleSelectLabel,
    handleCreateLabel,
    handleRemoveLabel,
    handleDeleteLabel,
  } = useNodeLabels({ nodeId, mindmapId, labels })

  // Canvas export (download PNG / copy to clipboard)
  const { handleDownloadPNG, handleCopyToClipboard } = useStoryboardCanvasExport({
    storyboardUrls: nodeData.storyboardUrls,
    frameCaptions: nodeData.frameCaptions,
    storyboardTitle: nodeData.storyboardTitle,
  })

  // Zoom state for collapsed view and button scaling
  const { isCollapsed, zoomScaleRaw, zoomScale } = useZoomState()

  // Track which panels failed to load
  const storyboardUrls = nodeData.storyboardUrls
  const [panelErrors, setPanelErrors] = useState([false, false, false, false])
  useEffect(() => {
    setPanelErrors([false, false, false, false])
  }, [storyboardUrls])

  const handlePanelError = useCallback((index: number) => {
    setPanelErrors((prev) => {
      const next = [...prev]
      next[index] = true
      return next
    })
  }, [])

  // Derived values
  const displayTitle = (nodeData.storyboardTitle ?? nodeData.parentLabel) || 'Storyboard'
  const isGenerating = nodeData.isGenerating ?? false

  const handleClick = useCallback(() => {
    if (nodeData.isGenerating) return
    viewStoryboardNode(nodeId)
  }, [nodeId, nodeData.isGenerating, viewStoryboardNode])

  // Shared props passed to both view components
  const sharedProps = {
    nodeId,
    zoomScale,
    displayTitle,
    isReadOnly,
    labelsEnabled,
    labels,
    mindmapId,
    labelContext,
    onDeleteNode: handleDeleteNode,
    onSelectLabel: handleSelectLabel,
    onCreateLabel: handleCreateLabel,
    onRemoveLabel: handleRemoveLabel,
    onDeleteLabel: handleDeleteLabel,
  }

  if (isCollapsed) {
    return (
      <StoryboardCollapsedView
        zoomScaleRaw={zoomScaleRaw}
        isGenerating={isGenerating}
        onClickNode={handleClick}
      />
    )
  }

  if (isGenerating) {
    return <StoryboardGeneratingView {...sharedProps} />
  }

  return (
    <StoryboardFullView
      {...sharedProps}
      storyboardUrls={storyboardUrls}
      frameCaptions={nodeData.frameCaptions}
      panelErrors={panelErrors}
      onPanelError={handlePanelError}
      onClickNode={handleClick}
      onDownloadPNG={handleDownloadPNG}
      onCopyToClipboard={handleCopyToClipboard}
    />
  )
})

StoryboardNode.displayName = 'StoryboardNode'
